import {
  getPortalTenantN8nExtras,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  PORTAL_DOCUMENT_UPLOAD_URL,
  PORTAL_LITIGATION_JOB_STATUS_URL_TEMPLATE,
  PORTAL_LITIGATION_JOB_SUBMIT_URL,
  resolveOnlyofficeIssueJwtUrl,
} from '../../constants';
import {
  parsePortalDocumentN8nResponse,
  type PortalDocumentOpenFields,
} from './portalDocuments';

export type JudicatureJobStatusName = 'queued' | 'running' | 'succeeded' | 'failed';
export type JudicatureArtifactFormat = 'docx' | 'pdf';

export interface JudicatureArtifactRef {
  artifact_id: string;
  format: JudicatureArtifactFormat;
  relative_path: string;
  bytes: number;
}

export interface JudicatureGateViolation {
  code: string;
  section?: string | null;
  claim_id?: string | null;
  source_ref?: string | null;
}

export interface JudicatureLitigationJob {
  job_id: string;
  status: JudicatureJobStatusName;
  attempt: number;
  max_attempts: number;
  error_code?: string | null;
  exportable: boolean;
  artifacts: JudicatureArtifactRef[];
  gate_violations: JudicatureGateViolation[];
  human_review_required: true;
  system_is_legal_advice: false;
  updated_at?: string;
}

interface ArtifactDownloadPayload {
  ok: true;
  job_id: string;
  format: JudicatureArtifactFormat;
  filename: string;
  media_type: string;
  bytes: number;
  artifact_id: string;
  data_base64: string;
  synthetic_test_data: true;
}

export class JudicatureJobApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
  ) {
    super(code);
    this.name = 'JudicatureJobApiError';
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function unwrapPayload(value: unknown): Record<string, unknown> {
  const record = asRecord(value);
  const payload = asRecord(record.payload);
  return Object.keys(payload).length ? payload : record;
}

function errorCode(payload: Record<string, unknown>, status: number): string {
  const detail = asRecord(payload.detail);
  const code = payload.error_code ?? detail.error_code;
  return typeof code === 'string' && code.trim()
    ? code.trim()
    : `HTTP_${status}`;
}

async function parseJsonResponse(res: Response): Promise<Record<string, unknown>> {
  let body: unknown = {};
  try {
    body = await res.json();
  } catch {
    body = {};
  }
  const payload = unwrapPayload(body);
  if (!res.ok || payload.ok === false) {
    throw new JudicatureJobApiError(errorCode(payload, res.status), res.status);
  }
  return payload;
}

/**
 * Bearer aus dem PB-Token (`portal-authz`), nicht mehr das Upload-Secret. Der
 * Ziel-URL muss mit, weil `portalAuthzHeaders` nur auf den geschützten Pfaden
 * einen Header setzt — Submit, Status und Artefakt-Download stehen alle drauf.
 */
function portalJobHeaders(url: string): Record<string, string> {
  if (!hasPortalAuthzToken()) {
    throw new JudicatureJobApiError('PORTAL_AUTHZ_TOKEN_MISSING', 401);
  }
  return {
    Accept: 'application/json',
    ...portalAuthzHeaders(url),
  };
}

function statusUrl(jobId: string): string {
  const template = PORTAL_LITIGATION_JOB_STATUS_URL_TEMPLATE;
  if (!template.includes('{job_id}')) {
    throw new JudicatureJobApiError('STATUS_URL_TEMPLATE_INVALID', 500);
  }
  return resolveOnlyofficeIssueJwtUrl(
    template.replace('{job_id}', encodeURIComponent(jobId)),
  );
}

function safeTenantId(customerId: string): string {
  const safe = customerId.replace(/[^A-Za-z0-9._:-]/g, '-').slice(0, 100);
  return `portal-${safe || 'labor-demo'}`;
}

function createJobId(): string {
  const randomPart =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `portal-demo-${Date.now()}-${randomPart}`.slice(0, 127);
}

function parseJob(payload: Record<string, unknown>): JudicatureLitigationJob {
  const status = payload.status;
  if (
    typeof payload.job_id !== 'string'
    || !['queued', 'running', 'succeeded', 'failed'].includes(String(status))
  ) {
    throw new JudicatureJobApiError('JOB_RESPONSE_INVALID', 502);
  }
  return payload as unknown as JudicatureLitigationJob;
}

export async function startJudicatureDemoJob(
  customerId: string,
): Promise<JudicatureLitigationJob> {
  const submitUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_LITIGATION_JOB_SUBMIT_URL);
  const res = await fetch(submitUrl, {
    method: 'POST',
    headers: {
      ...portalJobHeaders(submitUrl),
      'Content-Type': 'application/json',
    },
    credentials: 'omit',
    body: JSON.stringify({
      schema_version: 'portal-litigation-demo.v1',
      job_id: createJobId(),
      tenant_id: safeTenantId(customerId),
      workspace_reference: 'portal-labor-demo',
      demo_case: 'mietrecht-befristung',
      formats: ['docx', 'pdf'],
    }),
    signal: AbortSignal.timeout(60_000),
  });
  return parseJob(await parseJsonResponse(res));
}

export async function getJudicatureDemoJob(
  jobId: string,
): Promise<JudicatureLitigationJob> {
  const url = statusUrl(jobId);
  const res = await fetch(url, {
    headers: portalJobHeaders(url),
    credentials: 'omit',
    cache: 'no-store',
    signal: AbortSignal.timeout(30_000),
  });
  return parseJob(await parseJsonResponse(res));
}

function base64ToBlob(payload: ArtifactDownloadPayload): Blob {
  const binary = window.atob(payload.data_base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: payload.media_type });
}

export async function fetchJudicatureDemoArtifact(
  jobId: string,
  format: JudicatureArtifactFormat,
): Promise<{ blob: Blob; filename: string }> {
  const url = new URL(statusUrl(jobId), window.location.origin);
  url.searchParams.set('artifact', format);
  const res = await fetch(url.toString(), {
    headers: portalJobHeaders(url.toString()),
    credentials: 'omit',
    cache: 'no-store',
    signal: AbortSignal.timeout(60_000),
  });
  const payload = await parseJsonResponse(res) as unknown as ArtifactDownloadPayload;
  if (
    payload.ok !== true
    || payload.synthetic_test_data !== true
    || payload.format !== format
    || typeof payload.data_base64 !== 'string'
  ) {
    throw new JudicatureJobApiError('ARTIFACT_RESPONSE_INVALID', 502);
  }
  const blob = base64ToBlob(payload);
  if (blob.size !== payload.bytes) {
    throw new JudicatureJobApiError('ARTIFACT_SIZE_MISMATCH', 502);
  }
  return { blob, filename: payload.filename };
}

export async function uploadJudicatureDemoDocxToDocuments(args: {
  jobId: string;
  customerId: string;
  customerStatus?: string;
  customerEmail?: string;
}): Promise<PortalDocumentOpenFields> {
  const artifact = await fetchJudicatureDemoArtifact(args.jobId, 'docx');
  const file = new File([artifact.blob], artifact.filename, { type: artifact.blob.type });
  const formData = new FormData();
  formData.set('file', file);
  formData.set('customerId', args.customerId);
  formData.set('folderPath', 'litigation-analysis');
  const tenantExtras = getPortalTenantN8nExtras(args.customerStatus, args.customerEmail);
  if (tenantExtras.tenant) formData.set('tenant', tenantExtras.tenant);

  const uploadUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_UPLOAD_URL);
  const res = await fetch(uploadUrl, {
    method: 'POST',
    headers: portalJobHeaders(uploadUrl),
    credentials: 'omit',
    body: formData,
    signal: AbortSignal.timeout(120_000),
  });
  let data: Record<string, unknown> = {};
  try {
    data = asRecord(await res.json());
  } catch {
    data = {};
  }
  return parsePortalDocumentN8nResponse(data, res);
}
